/**
 * The Elevate content governance surface, as the back office calls it.
 *
 * Every function here is one migration 262 RPC and nothing more. They live in
 * the `public` schema deliberately: the shared frontend client pins
 * Accept-Profile to `public`, and the alternative — widening the migration 251
 * RLS policies on elevate.* so the back office could read the tables directly —
 * would put a second condition into the same permissive OR that decides what a
 * TRAINEE sees. The 251 posture is worth more than the convenience.
 *
 * REFUSALS ARE NOT TRANSLATED. The database raises named errors
 * (ERR_APPROVER_NOT_AUTHORISED, ERR_APPROVAL_MISSING,
 * ERR_INVALID_STATUS_TRANSITION, ...) from the migration 237 guards, and the
 * screen shows them as they arrive. A governance refusal is evidence: the
 * person reading it needs the rule's own words, not a friendlier paraphrase.
 *
 * The CLIs from HP-1B (scripts/approve-content.ts, scripts/publish-content.ts)
 * remain the operational path for wave runs and are unaffected by this module.
 */
import { supabase } from './supabase';
import type {
  ContentApproverRow,
  ContentLifecycleStatus,
  GateCountRow,
  GateTargetRow,
  GovernanceActionResult,
  PublicationVerificationRow,
  ReviewQueueRow,
} from './elevateGovernance';

/**
 * Postgres raises the guard's message; PostgREST hands it back on `message`.
 * Rethrowing it unchanged is the whole contract of this module — see the file
 * header. `details` is appended where present because the guard's hints
 * sometimes carry the object id the message does not.
 */
function refuse(error: { message: string; details?: string | null } | null): void {
  if (!error) return;
  throw new Error(error.details ? `${error.message} (${error.details})` : error.message);
}

/** GOV-02, DEC-09: does the signed-in person hold the WingMentor-approver role? */
export async function fetchIsContentApprover(): Promise<boolean> {
  const { data, error } = await supabase.rpc('elevate_is_content_approver');
  refuse(error);
  return data === true;
}

export async function fetchReviewQueue(filters?: {
  status?: ContentLifecycleStatus | null;
  contentPackId?: string | null;
}): Promise<ReviewQueueRow[]> {
  const { data, error } = await supabase.rpc('elevate_content_review_queue', {
    p_status: filters?.status ?? null,
    p_pack: filters?.contentPackId ?? null,
  });
  refuse(error);
  return (data ?? []) as ReviewQueueRow[];
}

export async function fetchPublicationVerification(
  competencyCode?: string | null,
): Promise<PublicationVerificationRow[]> {
  const { data, error } = await supabase.rpc('elevate_publication_verification', {
    p_competency: competencyCode ?? null,
  });
  refuse(error);
  return (data ?? []) as PublicationVerificationRow[];
}

export async function fetchGateCounts(): Promise<GateCountRow[]> {
  const { data, error } = await supabase.rpc('elevate_gate_counts');
  refuse(error);
  return (data ?? []) as GateCountRow[];
}

export async function fetchGateTargets(): Promise<GateTargetRow[]> {
  const { data, error } = await supabase.rpc('elevate_gate_targets');
  refuse(error);
  return (data ?? []) as GateTargetRow[];
}

export async function fetchContentApprovers(): Promise<ContentApproverRow[]> {
  const { data, error } = await supabase.rpc('elevate_content_approvers');
  refuse(error);
  return (data ?? []) as ContentApproverRow[];
}

/** GOV-02 / GOV-03: bind an approval to (object, current content version). */
export async function approveContentObject(objectUuid: string): Promise<GovernanceActionResult> {
  const { data, error } = await supabase.rpc('elevate_approve_object', {
    p_object_uuid: objectUuid,
  });
  refuse(error);
  return data as GovernanceActionResult;
}

/** GOV-01 / GOV-02: Approved -> Published, behind the migration 237 guard. */
export async function publishContentObject(objectUuid: string): Promise<GovernanceActionResult> {
  const { data, error } = await supabase.rpc('elevate_publish_object', {
    p_object_uuid: objectUuid,
  });
  refuse(error);
  return data as GovernanceActionResult;
}
